import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  IconButton,
  Dialog,
  Button,
  Fade,
  Skeleton,
  Alert,
  Backdrop,
  CircularProgress,
  Select,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { message } from "antd";
import { motion, AnimatePresence } from "framer-motion";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DuoIcon from "@mui/icons-material/Duo";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import Close from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import VideocamIcon from "@mui/icons-material/Videocam";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import PhotoIcon from "@mui/icons-material/Photo";
import ApiCallService from "../../../Service/ApiCallService";
import { useTheme } from "@mui/material/styles";
import { usePermission } from "../../../permissions/usePermission";
import Spinner from "../../../components/common/Spinner";
import { useSelector } from "react-redux";
import { Popover, MenuItem } from "@mui/material";
import axios from "axios";

const MediaLibrary = () => {
  const { folderId, programId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const routeProgramId = programId || location.state?.programId || null;
  const [folder, setFolder] = useState(null);
  const [mediaMap, setMediaMap] = useState({});
  const [resolvedProgramId, setResolvedProgramId] = useState(routeProgramId);
  const [uploadFolderId, setUploadFolderId] = useState(folderId || null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("ALL");
  const [sort, setSort] = useState("recent");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadType, setUploadType] = useState("IMAGE");
  const [uploadFile, setUploadFile] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [docLoading, setDocLoading] = useState(true);
  const isProgramMediaMode = Boolean(routeProgramId) && !folderId;
  const titleProgramName = "Promotional Material";

  const isLoggedIn = Boolean(
    localStorage.getItem("refreshToken") && localStorage.getItem("userId"),
  );

  const userData = useSelector((state) => state.user);

  const theme = useTheme();
  const { canByKey } = usePermission();
  const canUpdateProgram = canByKey("PROGRAM_MANAGEMENT_PROGRAMS_UPDATE");
  const canUploadMedia = canByKey("PROGRAM_MANAGEMENT_PROGRAM_CONTENT_UPLOAD_MEDIA");
  const canDeleteMedia = canByKey("PROGRAM_MANAGEMENT_PROGRAM_CONTENT_DELETE_MEDIA");

  const mapResourceType = (fileType = "") => {
    const normalized = String(fileType).toUpperCase();
    if (normalized === "IMAGE") return "IMAGE";
    if (normalized === "VIDEO") return "VIDEO";
    return "DOCUMENT";
  };

  const mapResourcesToMedia = (resources = []) => {
    return resources.reduce(
      (acc, item, index) => {
        const type = mapResourceType(item?.fileType || item?.type);
        const normalized = {
          _id: item?._id || `resource-${index}`,
          fileName: item?.name || item?.fileName || `Resource ${index + 1}`,
          url: item?.fileUrl || item?.url || "",
          type,
          createdAt: item?.createdAt || new Date().toISOString(),
          size: item?.size || 0,
        };

        if (!normalized.url) return acc;
        if (type === "IMAGE") acc.images.push(normalized);
        else if (type === "VIDEO") acc.videos.push(normalized);
        else acc.documents.push(normalized);
        return acc;
      },
      { images: [], videos: [], documents: [] },
    );
  };

  const mergeMediaMaps = (primary = {}, secondary = {}) => {
    const mergeByUrl = (first = [], second = []) => {
      const seen = new Set();
      return [...first, ...second].filter((item) => {
        const key = item?.url || item?._id;
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    };

    return {
      images: mergeByUrl(primary.images, secondary.images),
      videos: mergeByUrl(primary.videos, secondary.videos),
      documents: mergeByUrl(primary.documents, secondary.documents),
    };
  };

  const fetchMediaData = useCallback(async () => {
    try {
      setLoading(true);
      const stateResources = Array.isArray(location.state?.resources)
        ? location.state.resources
        : [];

      if (!folderId && !routeProgramId && stateResources.length) {
        setFolder(false);
        setResolvedProgramId(null);
        setUploadFolderId(null);
        setMediaMap(mapResourcesToMedia(stateResources));
        return;
      }

      if (folderId) {
        const res = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/folders/${folderId}`,
        );
        const folderData = res?.data?.folder || null;
        setFolder(folderData);
        setResolvedProgramId(folderData?.programId || programId || null);
        setUploadFolderId(folderId);
        setMediaMap(res?.data?.media || {});
        return;
      }

      if (routeProgramId) {
        const programRes = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/programs/${routeProgramId}`,
        );
        const folders = programRes?.data?.folders || [];
        const programResources = Array.isArray(programRes?.data?.program?.promotionalMaterial)
          ? programRes.data.program.promotionalMaterial
          : [];
        const fallbackResources = stateResources.length ? stateResources : programResources;

        if (!folders.length && fallbackResources.length) {
          setFolder(false);
          setResolvedProgramId(routeProgramId);
          setUploadFolderId(null);
          setMediaMap(mapResourcesToMedia(fallbackResources));
          return;
        }

        const preferredFolder =
          folders.find(
            (item) => String(item?.icon || "").toLowerCase() === "promotional",
          ) ||
          folders.find((item) =>
            String(item?.name || "").toLowerCase().includes("promotional"),
          ) ||
          folders[0] ||
          null;

        const folderResponses = await Promise.all(
          folders.map((item) =>
            axios
              .get(`${process.env.REACT_APP_BASE_URL}/folders/${item._id}`)
              .then((response) => response?.data?.media || {})
              .catch(() => ({})),
          ),
        );

        const aggregated = folderResponses.reduce(
          (acc, media) => {
            acc.images.push(...(media.images || []));
            acc.videos.push(...(media.videos || []));
            acc.documents.push(...(media.documents || []));
            return acc;
          },
          { images: [], videos: [], documents: [] },
        );
        const fallbackMapped = mapResourcesToMedia(fallbackResources);
        const mergedMedia = mergeMediaMaps(aggregated, fallbackMapped);

        setFolder(false);
        setResolvedProgramId(routeProgramId);
        setUploadFolderId(preferredFolder?._id || null);
        setMediaMap(mergedMedia);
      }
    } catch (err) {
      setFolder(false);
      message.error("Failed to load media data.");
      console.error("Error fetching media data:", err);
    } finally {
      setLoading(false);
    }
  }, [folderId, routeProgramId, location.state]);

  useEffect(() => {
    fetchMediaData();
  }, [fetchMediaData]);

  const allMedia = useMemo(() => {
    return [
      ...(mediaMap.images || []),
      ...(mediaMap.videos || []),
      ...(mediaMap.documents || []),
    ];
  }, [mediaMap]);

  const filteredMedia = useMemo(() => {
    let data = [...allMedia];

    if (filter !== "ALL") {
      data = data.filter((item) => item.type === filter);
    }

    if (search) {
      data = data.filter((item) =>
        item.fileName?.toLowerCase().includes(search.toLowerCase()),
      );
    }

    if (sort === "name") {
      data.sort((a, b) => a.fileName.localeCompare(b.fileName));
    }

    if (sort === "size") {
      data.sort((a, b) => (b.size || 0) - (a.size || 0));
    }

    if (sort === "recent") {
      data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }

    return data;
  }, [allMedia, filter, search, sort]);

  const triggerDownload = (url, fileName) => {
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName || "resource");
    link.target = "_self";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownload = async (e, item) => {
    e.stopPropagation();

    if (!item?.url) {
      message.error("Download link is not available.");
      return;
    }

    try {
      const response = await fetch(item.url);
      if (!response.ok) {
        throw new Error("Unable to fetch file");
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      triggerDownload(blobUrl, item.fileName);
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch {
      // Fallback for URLs where blob fetch is blocked; still force same-tab download flow.
      triggerDownload(item.url, item.fileName);
    }
  };

  const handleUpload = async () => {
    if (!canUploadMedia) return;
    const currentProgramId = resolvedProgramId || folder?.programId || null;
    const currentFolderId = folderId || uploadFolderId || null;

    if (!uploadFile) {
      message.error("Please select a file");
      return;
    }
    if (!currentProgramId || !currentFolderId) {
      message.error("No media destination folder found for this program.");
      return;
    }
    if (uploadFile.size > 50 * 1024 * 1024) {
      message.error("File size exceeds 50MB limit");
      return;
    }
    try {
      setLoading(true);
      setUploadProgress(0);

      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("programId", currentProgramId);
      formData.append("folderId", currentFolderId);
      formData.append("type", uploadType);
      formData.append("userId", localStorage.getItem("userId"));

      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      let response = await ApiCallService.post(
        `${process.env.REACT_APP_BASE_URL}/uploadMedia`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      clearInterval(progressInterval);
      setUploadProgress(100);

      message.success("Media uploaded successfully");
      setUploadOpen(false);
      setUploadFile(null);
      setUploadProgress(0);

      await fetchMediaData();
    } catch (error) {
      message.error("Failed to upload media", error?.response?.data);
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const handleMoreClick = (e, item) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedMedia(item);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleDeleteConfirm = () => {
    setConfirmOpen(true);
    handleClosePopover();
  };

  const handleDeleteMedia = async () => {
    if (!canDeleteMedia) return;

    try {
      await ApiCallService.delete(
        `${process.env.REACT_APP_BASE_URL}/deleteMedia/${selectedMedia._id}`,
      );

      message.success("Media deleted successfully");

      await fetchMediaData();
    } catch (error) {
      message.error("Failed to delete media");
    } finally {
      setConfirmOpen(false);
      setSelectedMedia(null);
    }
  };

  const getMediaIcon = (type) => {
    switch (type) {
      case "IMAGE":
        return <PhotoIcon />;
      case "VIDEO":
        return <VideocamIcon />;
      case "DOC":
        return <InsertDriveFileIcon />;
      default:
        return <InsertDriveFileIcon />;
    }
  };

  const getMediaColor = (type) => {
    switch (type) {
      case "IMAGE":
        return "#4CAF50";
      case "VIDEO":
        return "#2196F3";
      case "DOC":
        return "#FF9800";
        case "DOCUMENT":
          return "#FF9800";
      default:
        return "#9E9E9E";
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 MB";
    const mb = bytes / 1024 / 1024;
    return mb < 1 ? `${(bytes / 1024).toFixed(1)} KB` : `${mb.toFixed(1)} MB`;
  };

  const getDocumentPreviewUrl = (url = "") => {
    const lowerUrl = String(url).toLowerCase();
    const encoded = encodeURIComponent(url);

    if (/\.pdf(\?|$)/.test(lowerUrl)) {
      return `https://docs.google.com/gview?url=${encoded}&embedded=true`;
    }

    if (/\.(doc|docx|ppt|pptx|xls|xlsx)(\?|$)/.test(lowerUrl)) {
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encoded}`;
    }

    return `https://docs.google.com/gview?url=${encoded}&embedded=true`;
  };

  const handleIframeLoad = () => {
    setLoading(false);
  };

  useEffect(()=> {
    console.log("filteredMedia ", filteredMedia);

  }, [filteredMedia])

  return (
    <Box
      sx={{ minWidth: "100%", bgcolor: "white", margin: "0px", padding: "0px" }}
    >
      <Spinner loading={loading} />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1.5, sm: 2.5 },
          background: "white",
          pt: { xs: 1.5, sm: 2, md: 2.5 },
          borderRadius: { xs: 0, sm: "0 0 16px 16px" },
          maxWidth: "1320px",
          mx: "auto",
          px: { xs: 2, sm: 3 },
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: { xs: 1, sm: 2 },
          }}
        >
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              width: 30,
              height: 30,
              border: "1px solid #d9d9d9",
              color: "#9e9e9e",
              backgroundColor: "#f8f8f8",
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Box>
            <Typography
              sx={{
                fontSize: { xs: 18, sm: 22 },
                fontWeight: 600,
                color: "#1f1f1f",
                lineHeight: 1.2,
              }}
            >
              {titleProgramName}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 12, sm: 13 },
                color: "#555",
                lineHeight: 1.3,
              }}
            >
              Poster, Video, Banner, Brochures & PPTs
            </Typography>
          </Box>
        </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          mb: { xs: 0, sm: 1.5 },
          ml: "auto",
          width: { xs: "100%", sm: "auto" },
        }}
      >
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      flexWrap: "wrap",
      justifyContent: { xs: "flex-start", sm: isLoggedIn ? "space-between" : "flex-end" },
    }}
  >
    {/* Search */}
    <TextField
      size="small"
      placeholder="Search"
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      InputProps={{
        startAdornment: (
          <SearchIcon sx={{ mr: 1, color: "grey", fontSize: 20 }} />
        ),
        sx: {
          bgcolor: "white",
          borderRadius: 1.5,
          height: 42,
          width: { xs: "100%", sm: 260 },
        },
      }}
      sx={{ minWidth: { xs: "100%", sm: 260 } }}
    />

    {/* Filter */}
    <Select
      size="small"
      value={filter}
      onChange={(e) => setFilter(e.target.value)}
      displayEmpty
      renderValue={(selected) => {
        const labelMap = {
          ALL: "All",
          IMAGE: "Images",
          VIDEO: "Videos",
          DOC: "Documents",
          DOCUMENT: "Documents",
        };
        return labelMap[selected] || "All";
      }}
      sx={{
        width: { xs: "100%", sm: 120 },
        borderRadius: "8px",
        fontSize: { xs: 12, sm: 13 },
        fontWeight: 500,
        bgcolor: "white",
        height: 42,
      }}
    >
      <MenuItem value="ALL">All</MenuItem>
      <MenuItem value="IMAGE">
        <ImageIcon sx={{ mr: 1, fontSize: 14 }} />
        Images
      </MenuItem>
      <MenuItem value="VIDEO">
        <DuoIcon sx={{ mr: 1, fontSize: 14 }} />
        Videos
      </MenuItem>
      <MenuItem value="DOCUMENT">
        <DescriptionIcon sx={{ mr: 1, fontSize: 14 }} />
        Documents
      </MenuItem>
    </Select>

    {canUpdateProgram && (
    <Button
      variant="outlined"
      onClick={() => resolvedProgramId && navigate(`/editProgram/${resolvedProgramId}`)}
      disabled={!resolvedProgramId}
      sx={{
        px: 2.5,
        py: 1,
        fontSize: 14,
        textTransform: "none",
      }}
    >
      Update
    </Button>
    )}

    
  </Box>
</Paper>

      </Box>

      {/* Controls Section */}
      <Box
        sx={{
          maxWidth: "1320px",
          mx: "auto",
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
        }}
      >
        {!loading && filteredMedia.length === 0 && isLoggedIn && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 6,
                textAlign: "center",
                borderRadius: 3,
                border: "2px dashed #e2e8f0",
                bgcolor: "#f8fafc",
                minHeight: 400,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {/* Animated Icon */}
              <motion.div
                animate={{ y: [0, -15, 0] }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: "50%",
                    bgcolor: "rgba(33, 150, 243, 0.1)",
                  }}
                >
                  <CloudUploadIcon
                    sx={{
                      fontSize: 64,
                      color: theme.palette.primary.main,
                    }}
                  />
                </Box>
              </motion.div>

              {/* Animated Text */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Typography
                  variant="h6"
                  color="text.secondary"
                  mb={1}
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "1.125rem", sm: "1.25rem" },
                  }}
                >
                  No media files found
                </Typography>
              </motion.div>

              {/* Animated Description */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.35 }}
              >
                <Typography variant="body2" color="text.secondary" mb={3}>
                  {search
                    ? "Try adjusting your search or filters"
                    : "Upload your first media file to get started"}
                </Typography>
              </motion.div>

              {/* Animated Button */}
              {!search && isLoggedIn && canUploadMedia && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    variant="contained"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => setUploadOpen(true)}
                    sx={{
                      py: 1.5,
                      px: 3,
                      fontSize: "1rem",
                      fontWeight: 500,
                      textTransform: "none",
                      borderRadius: 2,
                      boxShadow: "0 4px 12px rgba(33, 150, 243, 0.3)",
                      "&:hover": {
                        boxShadow: "0 6px 16px rgba(33, 150, 243, 0.4)",
                      },
                    }}
                  >
                    Upload Files
                  </Button>
                </motion.div>
              )}

              {/* Animated Floating Elements (Background) */}
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  pointerEvents: "none",
                  overflow: "hidden",
                  borderRadius: 3,
                }}
              >
                {[...Array(3)].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{
                      opacity: 0.1,
                      x: Math.random() * 100 - 50,
                      y: Math.random() * 100 - 50,
                    }}
                    animate={{
                      opacity: [0.05, 0.15, 0.05],
                      x: [
                        Math.random() * 100 - 50,
                        Math.random() * 200 - 100,
                        Math.random() * 100 - 50,
                      ],
                      y: [
                        Math.random() * 100 - 50,
                        Math.random() * 200 - 100,
                        Math.random() * 100 - 50,
                      ],
                    }}
                    transition={{
                      duration: 6 + i * 1,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    style={{
                      position: "absolute",
                      width: 100 + i * 30,
                      height: 100 + i * 30,
                      borderRadius: "50%",
                      border: `2px dashed ${theme.palette.primary.main}`,
                    }}
                  />
                ))}
              </Box>
            </Paper>
          </motion.div>
        )}

        {!loading && filteredMedia.length === 0 && !isLoggedIn && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 6,
                textAlign: "center",
                borderRadius: 3,
                border: "2px dashed #e2e8f0",
                bgcolor: "#f8fafc",
                minHeight: 360,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {/* Animated Icon */}
              <motion.div
                animate={{ y: [0, -12, 0] }}
                transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: "50%",
                    bgcolor: "rgba(25, 118, 210, 0.1)",
                  }}
                >
                  <CloudUploadIcon
                    sx={{
                      fontSize: 64,
                      color: theme.palette.primary.main,
                    }}
                  />
                </Box>
              </motion.div>

              {/* Title */}
              <Typography
                variant="h6"
                sx={{ fontWeight: 600 }}
                color="text.secondary"
                mb={1}
              >
                No media found
              </Typography>

              {/* Subtitle */}
              <Typography variant="body2" color="text.secondary" mb={2}>
                {isProgramMediaMode
                  ? "No media has been added for this program yet"
                  : "This folder does not have any media yet"}
              </Typography>
            </Paper>
          </motion.div>
        )}

        {/* Media Grid */}
        <AnimatePresence>
          {!loading && filteredMedia.length > 0 && (
            <Grid container spacing={3}>
              {filteredMedia.map((item, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={item._id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ y: -8 }}
                  >
                    <Paper
                      onClick={() => {
                        setPreview(item);
                        setDocLoading(true);
                      }}
                      sx={{
                        borderRadius: 3,
                        overflow: "hidden",
                        cursor: "pointer",
                        position: "relative",
                        transition: "all 0.3s ease",
                        border: "1px solid #e2e8f0",
                        "&:hover": {
                          boxShadow: "0 12px 24px rgba(0,0,0,0.15)",
                          transform: "translateY(-4px)",
                        },
                      }}
                    >
                      {/* Media Preview */}
                      <Box
                        sx={{
                          height: 180,
                          background: "#ebedf0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        {item.type === "IMAGE" && (
                          <img
                            src={item.url}
                            alt=""
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        )}

                        {item.type === "VIDEO" && (
                          <>
                            <video
                              src={item.url}
                              muted
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                            <Box
                              sx={{
                                position: "absolute",
                                inset: 0,
                                bgcolor: "rgba(0,0,0,0.3)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <VideocamIcon
                                sx={{ fontSize: 48, color: "white" }}
                              />
                            </Box>
                          </>
                        )}

                        {item.type === "DOC" ||  item.type === "DOCUMENT" && (
                          <Box sx={{ textAlign: "center" }}>
                            <InsertDriveFileIcon
                              sx={{
                                fontSize: 64,
                                color: getMediaColor(item.type),
                              }}
                            />
                          </Box>
                        )}
                      </Box>

                      {/* Card Content */}
                      <Box sx={{ p: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 1,
                              bgcolor: getMediaColor(item.type),
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                            }}
                          >
                            {getMediaIcon(item.type)}
                          </Box>
                          <Typography
                            fontWeight={600}
                            fontSize={14}
                            sx={{ flex: 1 }}
                            noWrap
                          >
                            {item.fileName}
                          </Typography>

                          {isLoggedIn && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMoreClick(e, item);
                              }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          )}
        </AnimatePresence>
      </Box>
      <Dialog
        open={!!preview}
        onClose={() => setPreview(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 5,
          },
        }}
      >
        {/* HEADER */}
        <DialogTitle sx={{ p: 1 }}>
          <IconButton
            aria-label="close"
            onClick={() => {
              setPreview(null);
              setDocLoading(false);
            }}
            sx={{
              position: "absolute",
              right: 8,
              top: 4,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        {/* CONTENT */}
        <DialogContent
          sx={{
            pl: 4,
            pr: 4,
            pt: -4,
            pb: 0,
            mt: 2,
            height: "80vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "white",
          }}
        >
          {/* IMAGE */}
          {preview?.type === "IMAGE" && (
            <img
              src={preview.url}
              alt="Preview"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          )}

          {/* VIDEO */}
          {preview?.type === "VIDEO" && (
            <video
              src={preview.url}
              controls
              autoPlay
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          )}

          {/* DOCUMENT */}
          {preview?.type === "DOC" || preview?.type === "DOCUMENT" && (
            <>
              {docLoading && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "white",
                    zIndex: 1000,
                  }}
                >
                  <CircularProgress />
                </Box>
              )}

              <iframe
                title="PDF Viewer"
                src={getDocumentPreviewUrl(preview.url)}
                onLoad={() => setDocLoading(false)}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
              />
            </>
          )}
        </DialogContent>

        {/* FOOTER */}
        <DialogActions>
          {preview?.type === "DOC" || preview?.type === "DOCUMENT"  && (
            <Button
              variant="outlined"
              onClick={(e) => handleDownload(e, preview)}
            >
              Download
            </Button>
          )}
          <Button onClick={() => setPreview(null)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <Box sx={{ p: 4 }}>
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                bgcolor: `${theme.palette.primary.main}15`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <CloudUploadIcon
                sx={{
                  fontSize: 32,
                  color: theme.palette.primary.main,
                }}
              />
            </Box>
            <Typography variant="h5" fontWeight="bold" mb={1}>
              Upload Media
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose a file to upload to your media library
            </Typography>
          </Box>

          {/* Media Type Selection */}
          <TextField
            select
            fullWidth
            label="Media Type"
            value={uploadType}
            SelectProps={{ native: true }}
            onChange={(e) => setUploadType(e.target.value)}
            sx={{ mb: 3 }}
          >
            <option value="IMAGE">Image</option>
            <option value="VIDEO">Video</option>
            <option value="DOC">Document</option>
          </TextField>

          {/* File Upload Area */}
          <Box
            sx={{
              border: "2px dashed #e2e8f0",
              borderRadius: 2,
              p: 4,
              textAlign: "center",
              bgcolor: "#f8fafc",
              mb: 3,
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                borderColor: theme.palette.primary.main,
                bgcolor: `${theme.palette.primary.main}05`,
              },
            }}
            component="label"
          >
            <input
              type="file"
              hidden
              accept={
                uploadType === "IMAGE"
                  ? "image/*"
                  : uploadType === "VIDEO"
                    ? "video/*"
                    : ".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
              }
              onChange={(e) => setUploadFile(e.target.files[0])}
            />
            {uploadFile ? (
              <Box>
                <InsertDriveFileIcon
                  sx={{
                    fontSize: 48,
                    color: getMediaColor(uploadType),
                    mb: 1,
                  }}
                />
                <Typography fontWeight={500} mb={1}>
                  {uploadFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatFileSize(uploadFile.size)}
                </Typography>
              </Box>
            ) : (
              <Box>
                <CloudUploadIcon
                  sx={{
                    fontSize: 48,
                    color: "text.secondary",
                    mb: 1,
                  }}
                />
                <Typography fontWeight={500} mb={1}>
                  Click to browse or drag and drop
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {uploadType === "IMAGE"
                    ? "PNG, JPG, GIF up to 50MB"
                    : uploadType === "VIDEO"
                      ? "MP4, AVI, MOV up to 50MB"
                      : "PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX up to 50MB"}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Upload Progress */}
          {uploadProgress > 0 && (
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography variant="body2" fontWeight={500}>
                  Uploading...
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {uploadProgress}%
                </Typography>
              </Box>
              <Box
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: "#e2e8f0",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    bgcolor: theme.palette.primary.main,
                    transition: "width 0.3s ease",
                    width: `${uploadProgress}%`,
                  }}
                />
              </Box>
            </Box>
          )}

          {/* Actions */}
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button
              onClick={() => setUploadOpen(false)}
              disabled={uploadProgress > 0}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={!uploadFile || uploadProgress > 0}
              startIcon={<CloudUploadIcon />}
            >
              {uploadProgress > 0 ? "Uploading..." : "Upload"}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Context Menu */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            minWidth: 180,
          },
        }}
      >
        <Box sx={{ py: 1 }}>
          {selectedMedia?.type == "DOC" || selectedMedia?.type == "DOCUMENT" && (
            <MenuItem
              onClick={() => {
                handleDownload(new Event("click"), selectedMedia);
                handleClosePopover();
              }}
              sx={{ gap: 2 }}
            >
              <DownloadIcon fontSize="small" />
              Download
            </MenuItem>
          )}

          {canDeleteMedia && (
            <MenuItem
              onClick={handleDeleteConfirm}
              sx={{ gap: 2, color: "error.main" }}
            >
              <DeleteIcon fontSize="small" />
              Delete
            </MenuItem>
          )}
        </Box>
      </Popover>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: "#fee2e2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <DeleteIcon
              sx={{
                fontSize: 32,
                color: "#ef4444",
              }}
            />
          </Box>

          <Typography variant="h6" fontWeight="bold" mb={1}>
            Delete Media File?
          </Typography>

          <Typography variant="body2" color="text.secondary" mb={3}>
            This will permanently delete "{selectedMedia?.fileName}". This
            action cannot be undone.
          </Typography>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleDeleteMedia}
              startIcon={<DeleteIcon />}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Dialog>

      <style jsx>{`
        .media-card:hover .media-card-actions {
          opacity: 1;
        }
      `}</style>
    </Box>
  );
};

export default MediaLibrary;

